/**
 * Floating void decor (M18b): sparse set-dressing drifting in the empty
 * air around the line — rock shoals and motes everywhere, biome-flavored
 * silhouettes near themed islands (ships off tropical coasts, a ghost in
 * spooky air, snowflakes in winter), rare space events (meteor showers,
 * a passing craft). Purely atmospheric:
 *
 *  - NEW LABELED FAMILY (`void-decor`): by the seed law, adding it
 *    perturbs no existing stream.
 *  - SEPARATE OUTPUT (`EndlessSegment.voidDecor`, never `placements`):
 *    the proxy path structurally cannot see it — non-collision by
 *    construction, and it claims nothing in occupancy (decor must never
 *    constrain the line).
 *  - DENSITY IS THE LIVELINESS LAW: 0-2 clusters a segment (expected
 *    ~1) plus a rare event ~0.08 with quiet-after suppression —
 *    distribution over a long stretch, never crowd density.
 *
 * Sites hang off the segment's own path samples: a lateral offset into
 * the mid band (8-16u, slightly below the line) or the far band
 * (18-40u, scaled up into fog silhouettes). Sites keep ≥CLEARANCE from
 * every path sample (main + alt), island cells, and any occupancy claim
 * at their column. Future segments can wander anywhere, so a far shoal
 * may eventually share air with track generated later — accepted
 * residual (sparse, non-colliding, reads as parallax).
 */
import type { LoopPath, Placement, Vec3 } from "../../shared/types.ts";
import { rngFamily } from "../rng.ts";
import type { Rng } from "../rng.ts";
import { segLength, segPoint } from "../geom.ts";
import { TRACK_SCALE } from "./scale.ts";
import type { OccView } from "./occupancy.ts";
import type { Island } from "./islands.ts";

/** Minimum world distance from any path sample / island cell. */
export const VOID_CLEARANCE = 6;
/** Cluster budget per segment (hard cap, the structural-scan law). */
export const VOID_MAX_PIECES = 14;
const EVENT_CHANCE = 0.08;

interface VoidGroup {
  ids: readonly string[];
  scale: readonly [number, number];
  weight: number;
  /** Composite floating-island SHARD (user correction: a bush or a
   * ground rock floating in air reads WRONG — the void wants things
   * that plausibly float). A shard is a torn-off islet: cliff block(s)
   * with a grass cap and a ragged quarter-course underside, every piece
   * sharing ONE float phase so the islet bobs as a unit. */
  shard?: boolean;
}

/** Always-on vocabulary: floating island shards + space debris — never
 * bare ground props (those read as set-dressing that forgot gravity). */
const GENERIC: readonly VoidGroup[] = [
  { ids: [], scale: [0.5, 1.1], weight: 3, shard: true },
  {
    ids: [
      "space-kit:meteor_half",
      "space-kit:rock_crystals",
      "space-kit:rock_crystalsLargeA",
    ],
    scale: [0.9, 1.6],
    weight: 1.5,
  },
];

/** Biome-flavored clusters, keyed by this segment's island biomes —
 * silhouettes that plausibly ride the air (ships, a ghost, snow), never
 * ground props. */
const BIOME_GROUPS: Record<string, readonly VoidGroup[]> = {
  tropical: [
    {
      ids: [
        "pirate-kit:ship-wreck",
        "pirate-kit:ship-ghost",
        "pirate-kit:ship-pirate-small",
        "pirate-kit:ship-small",
      ],
      scale: [0.6, 0.9],
      weight: 3,
    },
  ],
  spooky: [
    { ids: ["graveyard-kit:character-ghost"], scale: [1.2, 1.8], weight: 2.5 },
    { ids: ["graveyard-kit:crypt"], scale: [1.6, 2.4], weight: 1 },
  ],
  winter: [
    {
      ids: [
        "holiday-kit:snowflake-a",
        "holiday-kit:snowflake-b",
        "holiday-kit:snowflake-c",
      ],
      scale: [0.8, 1.4],
      weight: 3,
    },
  ],
};

/** Rare events: a drifting shoal train or a passing craft, far band. */
const EVENTS: readonly VoidGroup[] = [
  {
    ids: ["space-kit:meteor", "space-kit:meteor_detailed"],
    scale: [1.5, 2.6],
    weight: 3,
  },
  {
    ids: [
      "space-kit:craft_speederA",
      "space-kit:craft_miner",
      "space-kit:satelliteDish_detailed",
    ],
    scale: [1.2, 2.0],
    weight: 2,
  },
];

interface PathSample {
  p: Vec3;
  /** Unit lateral (up × tangent), horizontal. */
  lat: Vec3;
}

/** Coarse world-space samples of a path (~2u spacing). */
function samplePath(path: LoopPath, out: PathSample[]): void {
  for (const seg of path.segments) {
    for (const c of seg.curve) {
      const len = segLength(c);
      const n = Math.max(1, Math.round(len / 2));
      for (let k = 0; k <= n; k++) {
        const p = segPoint(c, k / n);
        const q = segPoint(c, Math.min(1, k / n + 0.01));
        const tx = q[0] - p[0];
        const tz = q[2] - p[2];
        const tl = Math.hypot(tx, tz);
        out.push({
          p,
          lat: tl > 1e-6 ? [-tz / tl, 0, tx / tl] : [1, 0, 0],
        });
      }
    }
  }
}

/** Pure event roll for a segment index — order-independent, so segment
 * i can consult i−1's outcome without generating it. */
function eventRolls(seed: string, index: number): boolean {
  return rngFamily(seed, "void-decor").child(`segment/${index}`).child("event")
    .chance(EVENT_CHANCE);
}

export function planVoidDecor(
  seed: string,
  index: number,
  path: LoopPath,
  altPath: LoopPath | undefined,
  islands: readonly Island[],
  occ: OccView,
): Placement[] {
  const rng: Rng = rngFamily(seed, "void-decor").child(`segment/${index}`);
  const out: Placement[] = [];

  const samples: PathSample[] = [];
  samplePath(path, samples);
  if (altPath) samplePath(altPath, samples);
  if (samples.length === 0) return out;

  const isleCells: [number, number][] = [];
  for (const isle of islands) {
    for (const c of isle.cells) {
      const [x, z] = c.split(",").map(Number);
      isleCells.push([x, z]);
    }
  }

  const clear = (x: number, y: number, z: number): boolean => {
    for (const s of samples) {
      const dx = x - s.p[0];
      const dy = y - s.p[1];
      const dz = z - s.p[2];
      if (dx * dx + dy * dy + dz * dz < VOID_CLEARANCE * VOID_CLEARANCE) {
        return false;
      }
    }
    for (const [ix, iz] of isleCells) {
      const dx = x - ix;
      const dz = z - iz;
      if (dx * dx + dz * dz < VOID_CLEARANCE * VOID_CLEARANCE) return false;
    }
    // Occupancy column: past/current claims at this cell must not share
    // the site's band (future claims stay residual — decor claims none).
    const ty = y / TRACK_SCALE;
    const bands = occ.at(
      Math.round(x / TRACK_SCALE),
      Math.round(z / TRACK_SCALE),
    );
    for (const b of bands) {
      if (ty - 0.75 < b.y1 && b.y0 < ty + 0.75) return false;
    }
    return true;
  };

  const biomes = new Set(islands.map((isle) => isle.biome));
  const groups: [VoidGroup, number][] = GENERIC.map((
    g,
  ) => [g, g.weight] as [VoidGroup, number]);
  for (const b of biomes) {
    for (const g of BIOME_GROUPS[b] ?? []) groups.push([g, g.weight * 2]);
  }

  const emit = (
    r: Rng,
    group: VoidGroup,
    anchor: PathSample,
    side: number,
    far: boolean,
    count: number,
  ): void => {
    const latDist = far ? 18 + r.float() * 22 : 8 + r.float() * 8;
    const yOff = far ? -12 + r.float() * 20 : -6 + r.float() * 10;
    const cx = anchor.p[0] + anchor.lat[0] * latDist * side;
    const cz = anchor.p[2] + anchor.lat[2] * latDist * side;
    const cy = anchor.p[1] + yOff;
    for (let k = 0; k < count && out.length < VOID_MAX_PIECES; k++) {
      const px = cx + (r.float() - 0.5) * 6;
      const py = cy + (r.float() - 0.5) * 4;
      const pz = cz + (r.float() - 0.5) * 6;
      if (!clear(px, py, pz)) continue;
      const scale = group.scale[0] +
        r.float() * (group.scale[1] - group.scale[0]) * (far ? 1.4 : 1);
      const period = 6 + r.float() * 8;
      const drift = far && r.chance(0.6)
        ? [
          (r.float() - 0.5) * 0.24,
          0,
          (r.float() - 0.5) * 0.24,
        ] as Vec3
        : undefined;
      const float: NonNullable<Placement["float"]> = {
        amp: 0.1 + r.float() * 0.3,
        period,
        phase: r.float() * period,
        ...(drift ? { drift } : {}),
      };
      if (group.shard) {
        // Floating island shard: cliff course(s) + grass cap + a ragged
        // quarter underside, one rotation, ONE shared float (the islet
        // bobs and drifts as a body). Offsets scale with the shard (the
        // pieces' origins are bottom-center; a course is 1.0 authored).
        const mat = r.chance(0.6) ? "rock" : "stone";
        const tall = r.chance(0.3);
        const rot = r.int(0, 4) as Placement["rotation"];
        if (out.length + (tall ? 4 : 3) > VOID_MAX_PIECES) continue;
        // The whole islet must clear the line, cap and underside too.
        if (
          !clear(px, py + (tall ? 2 : 1) * scale, pz) ||
          !clear(px, py - 0.25 * scale, pz)
        ) continue;
        const shardPiece = (id: string, dy: number): Placement => ({
          pieceId: id,
          pos: [px, py + dy * scale, pz],
          rotation: rot,
          role: "decor",
          scale,
          float,
        });
        out.push(shardPiece(`nature-kit:cliff_blockQuarter_${mat}`, -0.25));
        out.push(shardPiece(`nature-kit:cliff_block_${mat}`, 0));
        if (tall) out.push(shardPiece(`nature-kit:cliff_block_${mat}`, 1));
        out.push(shardPiece("nature-kit:ground_grass", tall ? 2 : 1));
        continue;
      }
      out.push({
        pieceId: r.pick(group.ids as string[]),
        pos: [px, py, pz],
        rotation: r.int(0, 4) as Placement["rotation"],
        role: "decor",
        scale,
        float,
      });
    }
  };

  // Clusters: 0-2 a segment, expected ~1.
  const clusters = rng.child("clusters").weighted(
    [[0, 1.2], [1, 2.2], [2, 0.8]] as const,
  );
  for (let c = 0; c < clusters; c++) {
    const r = rng.child(`cluster/${c}`);
    const group = r.weighted(groups);
    const anchor = samples[r.int(0, samples.length)];
    emit(
      r,
      group,
      anchor,
      r.chance(0.5) ? 1 : -1,
      r.chance(0.45),
      1 + r.int(0, 5),
    );
  }

  // Rare far-band event, suppressed right after another event so two
  // never crowd consecutive segments (both rolls are pure in index).
  if (eventRolls(seed, index) && !(index > 0 && eventRolls(seed, index - 1))) {
    const r = rng.child("event-build");
    const group = r.weighted(EVENTS.map((g) => [g, g.weight] as const));
    const anchor = samples[r.int(0, samples.length)];
    emit(r, group, anchor, r.chance(0.5) ? 1 : -1, true, 2 + r.int(0, 4));
  }

  return out;
}
